/**
 * The stores referential, filtered by the stores selected in the top menu.
 * Admin only; read-only on the MTM platform.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PLATFORM, ROLES, type Platform } from '@/shared/constants';
import { getSelectedStoreIds } from '@/shared/config/app-config';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { useFillerRows } from '@/shared/ui/useFillerRows';
import plusIcon from '@/assets/images/plus.svg';
import {
  storesQueryOptions,
  workshopsQueryOptions,
  type StoreRecord,
} from './api';
import { StoreDialog } from './StoreDialog';
import { visibleStores } from './logic';

const REFRESH_DELAY_MS = 1000;

export function StoresPage({
  platform = PLATFORM.ABCDIOR,
}: Readonly<{
  platform?: Platform;
}>) {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const isAbcdior = platform === PLATFORM.ABCDIOR;
  const noEdit = platform === PLATFORM.DMS; // no editing of stores in DMS
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const stores = useQuery({ ...storesQueryOptions(), enabled: isAdmin });
  const { data: workshops = [] } = useQuery({
    ...workshopsQueryOptions(),
    enabled: isAdmin,
  });
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>(() =>
    getSelectedStoreIds(),
  );
  const [dialog, setDialog] = useState<{ item: StoreRecord | null } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const items = visibleStores(stores.data ?? [], platform, selectedIds);
  const fillerRows = useFillerRows(containerRef, 'tr.store-row', items.length);
  const workshopName = (id: string | null | undefined) =>
    workshops.find((workshop) => workshop.id === id)?.name ?? '';

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    setDialog(null);
    if (result === 'Confirm') {
      setTimeout(
        () =>
          void queryClient.invalidateQueries({
            queryKey: storesQueryOptions().queryKey,
          }),
        REFRESH_DELAY_MS,
      );
    }
  };

  if (!isAdmin) return null;

  const columns = isAbcdior
    ? [10, 10, 10, 20, 10, 15, 15, 10]
    : [10, 10, 10, 20, 10, 10];

  return (
    <div className="container summary">
      <TopMenu platform={platform} onStoreSelected={setSelectedIds} />
      <div className="summary-table my-12">
        {!noEdit && (
          <button
            type="button"
            className="add-btn mb-4 flex items-center justify-center self-center"
            data-testid="header-add-store"
            onClick={() => setDialog({ item: null })}
          >
            <img src={plusIcon} alt="" />
            <span className="pl-2">Add a store</span>
          </button>
        )}
        <div className="table-container">
          <div
            className="items-container"
            ref={containerRef}
            role="region"
            aria-label="Stores"
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>Store Code</th>
                  <th style={{ width: '10%' }}>Region</th>
                  <th style={{ width: '10%' }}>Country</th>
                  <th style={{ width: '20%' }}>Name</th>
                  <th style={{ width: '10%' }}>Currency</th>
                  {isAbcdior && <th style={{ width: '15%' }}>Workshop SAV</th>}
                  {isAbcdior && (
                    <th style={{ width: '15%' }}>Workshop Event</th>
                  )}
                  <th style={{ width: '10%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="store-row">
                    <td
                      style={{ width: '10%' }}
                      title={item.storeCode ?? ''}
                      data-testid="store-row-store-code"
                    >
                      {item.storeCode}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={item.region ?? ''}
                      data-testid="store-row-region"
                    >
                      {item.region}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={item.country ?? ''}
                      data-testid="store-row-country"
                    >
                      {item.country}
                    </td>
                    <td
                      style={{ width: '20%' }}
                      title={item.name ?? ''}
                      data-testid="store-row-name"
                    >
                      {item.name}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={item.currency ?? ''}
                      data-testid="store-row-currency"
                    >
                      {item.currency}
                    </td>
                    {isAbcdior && (
                      <td
                        style={{ width: '15%' }}
                        title={workshopName(item.workshopSavId)}
                        data-testid="store-row-workshop-sav"
                      >
                        {workshopName(item.workshopSavId)}
                      </td>
                    )}
                    {isAbcdior && (
                      <td
                        style={{ width: '15%' }}
                        title={workshopName(item.workshopEventId)}
                        data-testid="store-row-workshop-event"
                      >
                        {workshopName(item.workshopEventId)}
                      </td>
                    )}
                    <td style={{ width: '10%' }} className="edit-action">
                      <button
                        type="button"
                        className="button"
                        onClick={() => setDialog({ item })}
                        disabled={stores.isFetching}
                        data-testid="store-row-edit-button"
                      >
                        {noEdit ? 'view' : 'edit'}
                      </button>
                    </td>
                  </tr>
                ))}
                {Array.from({ length: fillerRows }, (_, index) => (
                  <tr key={`empty-${index}`} className="empty-row">
                    {columns.map((width, cell) => (
                      <td key={cell} style={{ width: `${width}%` }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {dialog && (
        <StoreDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          readOnly={noEdit}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
